import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import * as classesService from "../services/classes";
import * as lessonsService from "../services/lessons";
import * as messagesService from "../services/messages";
import "./TeacherStudents.css";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const LOCALES = { fr: "fr-FR", en: "en-GB", ar: "ar-TN" };

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

// Next time this lesson happens (weekly lessons repeat every 7 days).
function nextOccurrence(lesson, now) {
  const start = lesson?.scheduled_at ? new Date(lesson.scheduled_at).getTime() : NaN;
  if (!Number.isFinite(start) || lesson.status === "cancelled") return null;
  if (start >= now) return start;
  if (!lesson.is_recurring_weekly) return null;
  return start + Math.ceil((now - start) / WEEK_MS) * WEEK_MS;
}

function initials(name) {
  const parts = String(name || "?").trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

// "Mes élèves" for teachers: every child they teach (lessons or classes), with
// the parent to contact, their classes and the next lesson.
export default function TeacherStudents() {
  const { accessToken, user } = useAuth();
  const { t, language } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";
  const tr = (key, options) => t(`teacherStudents.${key}`, options);
  const locale = LOCALES[language] || language || "fr-FR";

  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [classes, setClasses] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    Promise.all([
      messagesService.fetchContacts({ accessToken }).catch(() => ({ contacts: [] })),
      lessonsService.fetchLessons({ accessToken }).catch(() => ({ lessons: [] })),
      classesService.fetchClasses({ accessToken }).catch(() => ({ classes: [] })),
    ]).then(([c, l, g]) => {
      if (ignore) return;
      setContacts(asArray(c?.contacts));
      setLessons(asArray(l?.lessons || l?.results));
      setClasses(asArray(g?.classes || g?.results));
      setLoading(false);
    });
    return () => {
      ignore = true;
    };
  }, [accessToken]);

  const students = useMemo(() => {
    const now = Date.now();
    const byId = new Map();
    contacts.forEach((c) => {
      if (!byId.has(c.student_id)) {
        byId.set(c.student_id, {
          id: c.student_id,
          name: c.student_name,
          parent: { id: c.user_id, name: c.name },
          classes: [],
          lessons: 0,
          completed: 0,
          next: null,
        });
      }
    });
    const myClasses = classes.filter(
      (g) => Number(g.teacher?.id ?? g.teacher_id) === Number(user?.id),
    );
    myClasses.forEach((g) => {
      asArray(g.student_ids || asArray(g.students).map((s) => s.id)).forEach((id) => {
        const s = byId.get(Number(id));
        if (s && !s.classes.includes(g.name)) s.classes.push(g.name);
      });
    });
    lessons.forEach((lesson) => {
      const s = byId.get(Number(lesson.student?.id ?? lesson.student_id));
      if (!s) return;
      s.lessons += 1;
      if (lesson.status === "completed") s.completed += 1;
      const at = nextOccurrence(lesson, now);
      if (at && (!s.next || at < s.next.at)) s.next = { at, title: lesson.title };
    });
    return [...byId.values()].sort((a, b) => {
      if (a.next && b.next) return a.next.at - b.next.at;
      if (a.next || b.next) return a.next ? -1 : 1;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [classes, contacts, lessons, user?.id]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) =>
      [s.name, s.parent.name, ...s.classes].join(" ").toLowerCase().includes(q),
    );
  }, [search, students]);

  const formatNext = (at) => {
    const date = new Date(at);
    const day = date.toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" });
    const time = date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
    return `${day} · ${time}`;
  };

  return (
    <main className="teacher-students" dir={dir}>
      <div className="teacher-students__shell">
        <header className="teacher-students__header">
          <div>
            <h1>{tr("title")}</h1>
            {!loading ? <p>{tr("subtitle", { count: students.length })}</p> : null}
          </div>
          <div className="teacher-students__search">
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tr("search")}
              aria-label={tr("search")}
            />
          </div>
        </header>

        {loading ? (
          <div className="teacher-students__spinner" aria-hidden="true" />
        ) : !students.length ? (
          <p className="teacher-students__empty">{tr("empty")}</p>
        ) : !visible.length ? (
          <p className="teacher-students__empty">{tr("noMatch")}</p>
        ) : (
          <ul className="teacher-students__grid">
            {visible.map((s) => (
              <li className="teacher-students__card" key={s.id}>
                <div className="teacher-students__top">
                  <span className="teacher-students__avatar">{initials(s.name)}</span>
                  <div className="teacher-students__who">
                    <strong>{s.name}</strong>
                    <small>{tr("parent", { name: s.parent.name })}</small>
                  </div>
                </div>

                {s.classes.length ? (
                  <ul className="teacher-students__classes">
                    {s.classes.map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                ) : null}

                <dl className="teacher-students__facts">
                  <div>
                    <dt>{tr("nextLesson")}</dt>
                    <dd>{s.next ? formatNext(s.next.at) : tr("none")}</dd>
                  </div>
                  <div>
                    <dt>{tr("lessonsDone")}</dt>
                    <dd>
                      {s.completed} / {s.lessons}
                    </dd>
                  </div>
                </dl>

                <div className="teacher-students__actions">
                  <Link
                    className="btn primary"
                    to={`/messages?to=${s.parent.id}&student=${s.id}`}
                  >
                    <i className="fa-regular fa-comments" aria-hidden="true" />
                    {tr("writeParent")}
                  </Link>
                  <Link className="btn ghost" to={`/homeworks`}>
                    <i className="fa-solid fa-book" aria-hidden="true" />
                    {tr("homework")}
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
